/**
 * /my-family — the parent-only landing page (Plan 06).
 *
 * Per the canonical plan, this page is the single source of truth
 * for "who's on this household." It surfaces three things in this
 * order:
 *
 *   1. **Guardians** — the ADULTS on the household (`HouseholdAccess`), each
 *      with their role and a three-dots menu into `<EditMemberSheet />`.
 *      NOTE: not the Prisma `AccountMember` model, which is the students
 *      below. Renders first, per the household-UX sketches.
 *
 *   2. **Students** — a compact list, one row per linked
 *      student (avatar + name + a one-line school/grade summary +
 *      an inline Edit action that opens `<EditStudentSheet />`),
 *      followed by an "Add student" tile.
 *
 *   3. **Auto-Order** — per-student auto-order status + entry points
 *      into the set-up wizard / manage page.
 *
 *   Adding students is a single entry point: the "Add student" CTA
 *   opens a chooser (`AddMemberSheet`) that branches into "Use a
 *   school code" (household-level activation-code claim for enrolled
 *   students) vs "Add manually" (the single-student form). There is
 *   no separate standalone claim card.
 *
 *   **Empty state** — when the household exists but has no students
 *   yet, a friendly "Add your first student" CTA replaces the rows.
 *   Accounts with no household at all render the shared
 *   `<NoHouseholdState />` (also used by /billing).
 *
 * Per Plan 06's mockup-feedback rules:
 *
 *   - No balance widget here — that's `/billing` (Plan 09).
 *   - No "Lunch Times" management surface — that's school-admin
 *     (Plan 13). The student card shows lunch period as read-only
 *     when present.
 *   - All primary CTAs use `<Button variant="default">` (Ordo blue).
 *
 * Loading state is the block-for-block `<MyFamilySkeleton />`.
 * Fetch errors surface as a sonner toast + retry button (never a
 * blank screen).
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { EmptyState } from '@ordo/ui/empty-state'
import type { SearchableSelectOption } from '@ordo/ui/searchable-select'
import { toast } from '@ordo/ui/sonner'
import { Plus, Users } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

import { useAccountCoOwnersQuery } from '@/api/co-owners'
import {
  listStudentsNeedingRequiredInfo,
  type MyFamilyMember,
  type MyFamilyPendingClaim,
  selectDisputeBlock,
  selectPaymentFailure,
  useMyFamilyQuery,
  useTagsByKindQuery,
} from '@/api/family'
import { AddMemberSheet } from '@/components/family/add-member-sheet'
import { AutoOrderCard } from '@/components/family/auto-order-card'
import { CompleteStudentInfoSheet } from '@/components/family/complete-student-info-sheet'
import { DisputeBlockBanner } from '@/components/family/dispute-block-banner'
import { type EditStudentSection, EditStudentSheet } from '@/components/family/edit-student-sheet'
import { getDisplayName, getInitials, listHouseholdStudents } from '@/components/family/helpers'
import { HouseholdMembersCard } from '@/components/family/household-members-card'
import { MissingInfoBanner } from '@/components/family/missing-info-banner'
import { NoHouseholdState } from '@/components/family/no-household-state'
import { PaymentFailureBanner } from '@/components/family/payment-failure-banner'
import { OrdoPageContainer } from '@/components/ordo-page-container'
import { OrdoPageHeader } from '@/components/ordo-page-header'
import { UserAvatar } from '@/components/user-identity-card'
import { memberAcceptsParentOrders } from '@/lib/family-ordering-access'
import { MyFamilySkeleton } from '@/skeletons/my-family-skeleton'

export function MyFamilyPage() {
  const { data, isPending, error, refetch } = useMyFamilyQuery()

  // Allergen + dietary catalogs feed the edit + add sheets'
  // multi-selects. They're loaded eagerly so opening a sheet is
  // instant — tag lists are small + stable, so the upfront cost is
  // negligible.
  const allergenQuery = useTagsByKindQuery('ALLERGEN')
  const dietaryQuery = useTagsByKindQuery('DIETARY')

  const allergenOptions = useMemo<SearchableSelectOption[]>(
    () => (allergenQuery.data?.tags ?? []).map((t) => ({ value: t.id, label: t.name })),
    [allergenQuery.data],
  )
  const dietaryOptions = useMemo<SearchableSelectOption[]>(
    () => (dietaryQuery.data?.tags ?? []).map((t) => ({ value: t.id, label: t.name })),
    [dietaryQuery.data],
  )

  const [addSheetOpen, setAddSheetOpen] = useState(false)
  // Which view the add sheet opens to — every "Add student" affordance
  // (empty-state, add-row, and the post-signup auto-open) opens the Add
  // Student chooser (school code vs manual). The 'picker' mode (student vs
  // co-owner) has no live entry point: the Guardians card's "+ Add guardian"
  // opens `<InviteCoParentSheet />` directly.
  const [addSheetMode, setAddSheetMode] = useState<'picker' | 'student-method'>('picker')
  const [editingStudent, setEditingStudent] = useState<{
    member: MyFamilyMember
    section: EditStudentSection
  } | null>(null)

  // Claim-time gap-fill: students whose campus marks facts REQUIRED that
  // are still empty. Ordering for them is blocked server-side, so the page
  // banners it and the completion sheet fills them one student at a time.
  const studentsNeedingInfo = useMemo(() => listStudentsNeedingRequiredInfo(data), [data])
  const [completeSheetOpen, setCompleteSheetOpen] = useState(false)
  // A school-code claim just landed — once /family/me refreshes, auto-open
  // the completion sheet if any claimed/visible student is missing facts.
  const [completeAfterClaim, setCompleteAfterClaim] = useState(false)
  useEffect(() => {
    if (!completeAfterClaim || isPending) {
      return
    }
    setCompleteAfterClaim(false)
    if (studentsNeedingInfo.length > 0) {
      setCompleteSheetOpen(true)
    }
  }, [completeAfterClaim, isPending, studentsNeedingInfo])

  function openAdd(mode: 'picker' | 'student-method') {
    setAddSheetMode(mode)
    setAddSheetOpen(true)
  }

  // A parent with an existing household but no students yet gets the Add
  // Student chooser once (school code vs manual). Accounts with no household
  // land on the shared <NoHouseholdState /> CTA instead.
  //
  // A guardian can span MULTIPLE households (their own kids plus a claimed
  // school-provisioned household) — students are merged across all of them.
  // The co-owner management card stays scoped to the household the viewer
  // owns (falling back to the first accessible one).
  const households = useMemo(
    () => (data?.me?.ownedAccounts ?? []).filter((a) => a.kind === 'HOUSEHOLD' && !a.isArchived),
    [data],
  )
  const household = useMemo(
    () => households.find((a) => a.ownerUserId === data?.me?.id) ?? households[0] ?? null,
    [households, data],
  )
  // A student with a granted sign-in sees their household read-only:
  // no add-student, no co-owner management (parent-only affordances).
  const viewerIsStudent = households.length > 0 && households.every((a) => a.viewerRole === 'STUDENT')
  // MANAGE_STUDENTS decides every student-editing affordance on this page.
  // Shares `useAccountCoOwnersQuery`'s cache with the Members card below, so
  // this costs no extra request. Permissions start empty while it loads, which
  // hides the affordances until we know — the safe direction.
  const { data: householdAccess } = useAccountCoOwnersQuery(household?.id)
  const canManageStudents = (householdAccess?.viewer.permissions ?? []).includes('MANAGE_STUDENTS')
  // The account owner may be a member of their own household (a teacher who
  // self-orders); that self-diner is not a "student" — exclude them from the
  // student list + count so My Family only ever shows dependents.
  const members = useMemo(() => listHouseholdStudents(households, data?.me?.id), [households, data])
  const studentCount = members.length
  const autoOpenedRef = useRef(false)
  useEffect(() => {
    // Don't auto-open over a claim that's already in flight — the pending
    // row is the state the parent should see.
    if (
      !autoOpenedRef.current &&
      data?.me &&
      household &&
      !viewerIsStudent &&
      canManageStudents &&
      studentCount === 0 &&
      (data.me.pendingClaims ?? []).length === 0
    ) {
      autoOpenedRef.current = true
      openAdd('student-method')
    }
  }, [data, household, studentCount, viewerIsStudent, canManageStudents])

  // Deep link (?editStudent=<userId>) — /orders/family's confirm-campus CTA
  // lands here with that student's edit sheet open. Consumed once (the param
  // is stripped) so closing the sheet doesn't bounce it back open.
  const [searchParams, setSearchParams] = useSearchParams()
  const editStudentParam = searchParams.get('editStudent')
  useEffect(() => {
    if (!editStudentParam || isPending || !canManageStudents) {
      return
    }
    const member = households.flatMap((a) => a.members).find((m) => !m.isArchived && m.user?.id === editStudentParam)
    if (member) {
      setEditingStudent({ member, section: 'profile' })
    }
    setSearchParams(
      (prev) => {
        prev.delete('editStudent')
        return prev
      },
      { replace: true },
    )
  }, [editStudentParam, isPending, canManageStudents, households, setSearchParams])

  // Fetch error -> sonner toast (instead of swapping the whole page
  // for an error placard). The card grid stays visible (cached data
  // if any) and a "Retry" button surfaces so the user can recover
  // without a full reload.
  useEffect(() => {
    if (error) {
      toast.error("Couldn't load your family", {
        description: error.message,
        action: {
          label: 'Retry',
          onClick: () => {
            void refetch()
          },
        },
      })
    }
  }, [error, refetch])

  if (isPending && !data) {
    return <MyFamilySkeleton />
  }

  if (!data?.me) {
    return (
      <OrdoPageContainer>
        <OrdoPageHeader icon={Users} title="My Family" />
        <NoHouseholdState
          onAdded={() => {
            void refetch()
          }}
        />
      </OrdoPageContainer>
    )
  }

  const owner = data.me
  const pendingClaims = owner.pendingClaims ?? []

  // A claim waiting on school verification must stay visible even before a
  // household exists — only fall back to the no-household CTA when there is
  // truly nothing to show.
  if (!household && pendingClaims.length === 0) {
    return (
      <OrdoPageContainer>
        <OrdoPageHeader icon={Users} title="My Family" />
        <NoHouseholdState
          onAdded={() => {
            void refetch()
          }}
        />
      </OrdoPageContainer>
    )
  }

  return (
    <OrdoPageContainer>
      <OrdoPageHeader icon={Users} title="My Family" />

      {!viewerIsStudent &&
      canManageStudents &&
      members.some(memberAcceptsParentOrders) &&
      studentsNeedingInfo.length > 0 ? (
        <MissingInfoBanner students={studentsNeedingInfo} onFinishSetup={() => setCompleteSheetOpen(true)} />
      ) : null}

      {!viewerIsStudent && members.some(memberAcceptsParentOrders) ? (
        <PaymentFailureBanner paymentFailure={selectPaymentFailure(data)} />
      ) : null}
      {!viewerIsStudent && members.some(memberAcceptsParentOrders) ? (
        <DisputeBlockBanner disputeBlock={selectDisputeBlock(data)} />
      ) : null}

      {household && !viewerIsStudent && <HouseholdMembersCard accountId={household.id} viewerUserId={owner.id} />}

      <StudentsLinkedSection
        members={members}
        pendingClaims={pendingClaims}
        canAdd={!viewerIsStudent && canManageStudents}
        canEdit={canManageStudents}
        onAdd={() => openAdd('student-method')}
        onEdit={(member, section) => setEditingStudent({ member, section })}
      />

      <AutoOrderCard members={members} />

      <AddMemberSheet
        open={addSheetOpen}
        onOpenChange={setAddSheetOpen}
        initialMode={addSheetMode}
        allergenOptions={allergenOptions}
        dietaryOptions={dietaryOptions}
        onAdded={() => {
          void refetch()
        }}
        onClaimedStudents={() => setCompleteAfterClaim(true)}
      />

      <CompleteStudentInfoSheet
        open={completeSheetOpen}
        onOpenChange={setCompleteSheetOpen}
        students={studentsNeedingInfo}
        onDone={() => {
          void refetch()
        }}
      />

      <EditStudentSheet
        open={editingStudent !== null}
        onOpenChange={(next) => {
          if (!next) {
            setEditingStudent(null)
          }
        }}
        member={editingStudent?.member ?? null}
        section={editingStudent?.section ?? 'profile'}
        allergenOptions={allergenOptions}
        dietaryOptions={dietaryOptions}
      />
    </OrdoPageContainer>
  )
}

// ---------------------------------------------------------------------------
// Page chrome
// ---------------------------------------------------------------------------

function EmptyStudentsCard({ onAdd }: { onAdd: () => void }) {
  return (
    <EmptyState
      variant="plain"
      padding="sm"
      icon={<Users />}
      title="No students yet"
      description="Add your first student — with a school code, or by entering their details."
      action={
        <Button variant="default" onClick={onAdd}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          Add student
        </Button>
      }
    />
  )
}

function StudentsLinkedSection({
  members,
  pendingClaims,
  canAdd,
  canEdit,
  onAdd,
  onEdit,
}: {
  members: MyFamilyMember[]
  pendingClaims: MyFamilyPendingClaim[]
  /** MANAGE_STUDENTS — false for student viewers and for guardians. */
  canAdd: boolean
  /** MANAGE_STUDENTS — hides the per-row Edit affordance for guardians. */
  canEdit: boolean
  onAdd: () => void
  onEdit: (member: MyFamilyMember, section: EditStudentSection) => void
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Students</CardTitle>
        <p className="mt-1 text-sm text-muted-foreground">
          The students in this household and their school information.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {members.length === 0 && pendingClaims.length === 0 ? (
          canAdd ? (
            <EmptyStudentsCard onAdd={onAdd} />
          ) : (
            // A guardian (or a student viewer) sees the same emptiness without
            // a CTA they aren't allowed to complete.
            <EmptyState
              variant="plain"
              padding="sm"
              icon={<Users />}
              title="No students yet"
              description="An owner or co-parent can add students to this household."
            />
          )
        ) : (
          <>
            {members.map((member) => (
              <CompactStudentRow key={member.id} member={member} canEdit={canEdit} onEdit={onEdit} />
            ))}
            {pendingClaims.map((claim) => (
              <PendingClaimRow key={claim.id} claim={claim} />
            ))}
            {canAdd ? <AddStudentRow onAdd={onAdd} /> : null}
          </>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * A school-code / student claim still waiting on the school's verification —
 * rendered like a student row but with a "Pending" badge so the request is
 * visibly in flight rather than silently absent.
 */
function PendingClaimRow({ claim }: { claim: MyFamilyPendingClaim }) {
  const name = claim.studentName ?? `Student at ${claim.schoolName}`
  const where = claim.locationName ?? claim.schoolName
  return (
    <div className="flex items-center gap-3 rounded-lg border border-dashed border-border px-3 py-2.5">
      <UserAvatar name={name} initials={getInitials({ firstName: name, lastName: '' })} avatarColor="SKY" size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{name}</p>
        <p className="truncate text-xs text-muted-foreground">Waiting for school verification · {where}</p>
      </div>
      <Badge variant="secondary" className="shrink-0">
        Pending
      </Badge>
    </div>
  )
}

/** One linked student: avatar + name + a one-line summary, with an Edit action. */
function CompactStudentRow({
  member,
  canEdit,
  onEdit,
}: {
  member: MyFamilyMember
  canEdit: boolean
  onEdit: (member: MyFamilyMember, section: EditStudentSection) => void
}) {
  const user = member.user
  if (!user) {
    return null
  }
  const summary = studentPickerDescription(member)
  const dietarySummary = [
    user.allergenTags.length > 0 ? `Allergens: ${user.allergenTags.map((tag) => tag.name).join(', ')}` : null,
    user.dietaryTags.length > 0 ? `Dietary: ${user.dietaryTags.map((tag) => tag.name).join(', ')}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
      <UserAvatar
        name={getDisplayName(user)}
        initials={getInitials(user)}
        avatarUri={user.avatarUri}
        avatarColor={user.avatarColor}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{getDisplayName(user)}</p>
        <p className="truncate text-xs text-muted-foreground">{summary ?? 'Student'}</p>
        {dietarySummary && canEdit ? (
          <button
            type="button"
            onClick={() => onEdit(member, 'dietary')}
            className="block max-w-full truncate text-left text-xs text-muted-foreground underline-offset-2 hover:underline"
            title={dietarySummary}>
            {dietarySummary}
          </button>
        ) : dietarySummary ? (
          <p className="max-w-full truncate text-xs text-muted-foreground" title={dietarySummary}>
            {dietarySummary}
          </p>
        ) : null}
      </div>
      {canEdit ? (
        <button
          type="button"
          onClick={() => onEdit(member, 'profile')}
          className="shrink-0 text-sm font-medium text-foreground underline underline-offset-2 hover:text-muted-foreground">
          Edit
        </button>
      ) : null}
    </div>
  )
}

/** "Add student" tile rendered below the linked-student rows. */
function AddStudentRow({ onAdd }: { onAdd: () => void }) {
  return (
    <button
      type="button"
      onClick={onAdd}
      className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground">
      <Plus className="h-4 w-4" aria-hidden />
      Add student
    </button>
  )
}

function studentPickerDescription(member: MyFamilyMember): string | undefined {
  const user = member.user
  if (!user) {
    return undefined
  }
  const enrollment = user.enrollments[0] ?? null
  const schoolMembership =
    user.memberships.find(
      (membership) =>
        membership.profileCode === 'STUDENT' && !membership.isArchived && typeof membership.location?.name === 'string',
    ) ?? null
  const schoolName = schoolMembership?.location?.name ?? enrollment?.locationName ?? null
  const grade = enrollment?.gradeLevel ? `Grade ${enrollment.gradeLevel}` : null
  return [schoolName, grade].filter(Boolean).join(' · ') || undefined
}
